import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import LandingPage from './components/Onboarding/LandingPage';
import HelpPage from './components/Pages/HelpPage';
import ResetPassword from './components/Auth/ResetPassword';
import LoadingSpinner from './components/Common/LoadingSpinner';
import lazyWithRetry from './utils/lazyWithRetry';
import { useAuth } from './hooks/useAuth';
import { isLocalGuidedCoachingDesignReview } from './services/agent/guidedCoachingFeatureFlags';

const AboutPage = lazyWithRetry(() => import('./components/Pages/AboutPage'), 'public-about-page');
const PrivacyPolicy = lazyWithRetry(() => import('./components/Pages/PrivacyPolicy'), 'public-privacy-policy');
const TermsOfService = lazyWithRetry(() => import('./components/Pages/TermsOfService'), 'public-terms-of-service');
const SciencePage = lazyWithRetry(() => import('./components/Onboarding/SciencePage'), 'public-science-page');
const ReferencesPage = lazyWithRetry(() => import('./components/Onboarding/ReferencesPage'), 'public-references-page');
const GuidedCoachingDesignReview = lazyWithRetry(
  () => import('./components/AI/GuidedCoachingDesignReview'),
  'guided-coaching-design-review'
);

const MARKETING_PUBLIC_PAGES = new Set([
  'landing',
  'science',
  'references',
  'help',
  'resources',
  'contact',
  'about',
  'privacy',
  'terms',
]);

const readInitialPublicPage = () => {
  try {
    const params = new URLSearchParams(window.location.search || '');
    const requested = (params.get('page') || '').toLowerCase();
    if (MARKETING_PUBLIC_PAGES.has(requested)) {
      return requested;
    }
  } catch (_) {
    // ignore
  }
  return 'landing';
};

const buildPublicPageUrl = (page, options = {}) => {
  if (page === 'landing') return '/';
  const params = new URLSearchParams({ page });
  if (page === 'resources' && options.view === 'graph') params.set('view', 'graph');
  return `/?${params.toString()}`;
};

const AuthConfirmationPage = () => (
  <div className="auth-confirmation-page" style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    padding: '2rem'
  }}>
    <div className="auth-loading">
      <div className="spinner" style={{
        width: '3rem',
        height: '3rem',
        border: '4px solid #e5e7eb',
        borderTopColor: '#5E8C6A',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite'
      }} />
      <p style={{ marginTop: '1.5rem', fontSize: '1.125rem' }}>Verifying your email...</p>
      <p style={{ marginTop: '0.75rem', fontSize: '0.875rem', color: 'var(--text-secondary, #6b7280)' }}>
        You&apos;ll be redirected automatically.
      </p>
    </div>
  </div>
);

const PublicMarketingShell = ({ AuthenticatedApp }) => {
  const { isAuthenticated, isLoading, updatePassword } = useAuth();
  const [currentPage, setCurrentPage] = useState(readInitialPublicPage);
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
  const isLocalDesignReview = isLocalGuidedCoachingDesignReview({
    nodeEnv: process.env.NODE_ENV,
    pathname: currentPath,
    hostname: typeof window !== 'undefined' ? window.location.hostname : ''
  });

  const navigatePublicPage = useCallback((page, options = {}) => {
    const nextPage = MARKETING_PUBLIC_PAGES.has(page) ? page : 'landing';
    setCurrentPage(nextPage);

    if (typeof window === 'undefined' || !window.history?.pushState) return;
    const nextUrl = buildPublicPageUrl(nextPage, options);
    const currentUrl = `${window.location.pathname}${window.location.search}`;
    if (currentUrl === nextUrl) return;

    if (options.replace) {
      window.history.replaceState({ publicPage: nextPage }, '', nextUrl);
      return;
    }
    window.history.pushState({ publicPage: nextPage }, '', nextUrl);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handlePopState = () => {
      setCurrentPage(readInitialPublicPage());
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigateHome = () => navigatePublicPage('landing');
  const pageFallback = useMemo(() => <LoadingSpinner />, []);

  if (currentPath === '/auth/confirm') {
    return <AuthConfirmationPage />;
  }

  if (currentPath === '/auth/reset') {
    return (
      <ResetPassword
        onUpdatePassword={updatePassword}
        onGoToLogin={() => {
          window.location.href = '/';
        }}
      />
    );
  }

  if (isLocalDesignReview) {
    return (
      <Suspense fallback={<LoadingSpinner message="Opening design review..." />}>
        <GuidedCoachingDesignReview />
      </Suspense>
    );
  }

  if (isLoading) {
    return <LoadingSpinner message="Loading..." />;
  }

  if (isAuthenticated) {
    return (
      <Suspense fallback={<LoadingSpinner message="Opening workspace..." />}>
        <AuthenticatedApp />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={pageFallback}>
      {currentPage === 'science' && (
        <SciencePage
          onStartExperiment={navigateHome}
          onReadReferences={() => navigatePublicPage('references')}
          onNavigateHome={navigateHome}
          onNavigatePage={navigatePublicPage}
        />
      )}

      {currentPage === 'references' && (
        <ReferencesPage
          onBackToScience={() => navigatePublicPage('science')}
          onNavigateHome={navigateHome}
        />
      )}

      {(currentPage === 'help' || currentPage === 'resources' || currentPage === 'contact') && (
        <HelpPage
          onBack={navigateHome}
          onNavigateHome={navigateHome}
          onNavigatePage={navigatePublicPage}
          onStartApp={navigateHome}
          activePage={currentPage === 'resources' ? 'resources' : 'help'}
        />
      )}

      {currentPage === 'about' && (
        <AboutPage
          onBack={navigateHome}
          onGetStarted={navigateHome}
          onNavigateHome={navigateHome}
          onNavigatePage={navigatePublicPage}
          onStartApp={navigateHome}
        />
      )}

      {currentPage === 'privacy' && (
        <PrivacyPolicy
          onBack={navigateHome}
          onNavigateHome={navigateHome}
          onNavigatePage={navigatePublicPage}
          onStartApp={navigateHome}
        />
      )}

      {currentPage === 'terms' && (
        <TermsOfService
          onBack={navigateHome}
          onNavigateHome={navigateHome}
          onNavigatePage={navigatePublicPage}
          onStartApp={navigateHome}
        />
      )}

      {currentPage === 'landing' && (
        <LandingPage
          onGetStarted={navigateHome}
          onScienceClick={() => navigatePublicPage('science')}
          onHelpClick={() => navigatePublicPage('contact')}
          onGuideClick={() => navigatePublicPage('help')}
          onResourcesClick={() => navigatePublicPage('resources')}
          onKnowledgeGraphClick={() => navigatePublicPage('resources', { view: 'graph' })}
          onAboutClick={() => navigatePublicPage('about')}
          onPrivacyClick={() => navigatePublicPage('privacy')}
          onTermsClick={() => navigatePublicPage('terms')}
        />
      )}
    </Suspense>
  );
};

export default PublicMarketingShell;
